import React, { useEffect, useState } from 'react';
import {
  formatMonthYear, formatDayName, formatTime,
  isSameDay, isToday, isPast, groupSlotsByPeriod, getRecommendedTimes, getWeekDays
} from './utils';

function TimeSelectionStep({
  selectedCenter, weekStart, selectedDate, selectedTime, loadingSlots,
  availableSlots, showAutoAdvanceToast, autoAdvanceDate,
  onDismissToast, onWeekNav, onDateSelect, onTimeSelect, getDayAvailability,
  upkeepEligibility,
  preferencedServices = [], relaxedServiceLabels = [],
  onRelaxService, onRelaxAllServices, onResetRelaxed
}) {
  const [toastExiting, setToastExiting] = useState(false);
  const [softPromptDismissed, setSoftPromptDismissed] = useState(false);

  // Re-show the soft prompt after each new fetch / date change
  useEffect(() => { setSoftPromptDismissed(false); }, [selectedDate, relaxedServiceLabels.length]);

  useEffect(() => {
    if (!showAutoAdvanceToast) { setToastExiting(false); return; }
    setToastExiting(false);
    const slideOut = setTimeout(() => setToastExiting(true), 3700);
    const dismiss = setTimeout(() => onDismissToast?.(), 4000);
    return () => { clearTimeout(slideOut); clearTimeout(dismiss); };
  }, [showAutoAdvanceToast, onDismissToast]);

  function dismissToastNow() {
    setToastExiting(true);
    setTimeout(() => onDismissToast?.(), 250);
  }

  const Spinner = ({ text }) => (
    <div className="loading">
      <div className="spinner"></div>
      <span>{text || 'Loading...'}</span>
    </div>
  );

  // Upkeep: find earliest deadline across all eligible services
  const upkeepDeadline = upkeepEligibility?.length > 0
    ? new Date(upkeepEligibility.reduce((earliest, e) => e.deadline < earliest ? e.deadline : earliest, upkeepEligibility[0].deadline))
    : null;

  function isUpkeepDate(day) {
    if (!upkeepDeadline) return false;
    const d = new Date(day);
    d.setHours(23, 59, 59);
    return d <= upkeepDeadline;
  }

  function formatUpkeepBanner() {
    if (!upkeepEligibility?.length) return null;
    const deadline = new Date(upkeepEligibility[0].deadline);
    const dateStr = deadline.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const names = upkeepEligibility.map(e => e.serviceName);
    if (names.length === 1) {
      return `Book by ${dateStr} for your 10% upkeep discount on ${names[0]}`;
    }
    return `Book by ${dateStr} for your 10% upkeep discount on ${names.join(', ')}`;
  }

  return (
    <div className="step-content">
      <h1 className="step-title">CHOOSE YOUR TIME</h1>
      <p className="step-subtitle">{selectedCenter?.name}</p>

      {/* Upkeep discount banner */}
      {upkeepEligibility?.length > 0 && (
        <div className="upkeep-banner">
          <span className="upkeep-banner-icon">✦</span>
          <span>{formatUpkeepBanner()}</span>
        </div>
      )}

      {/* Auto-advance toast — slides up from bottom */}
      {showAutoAdvanceToast && autoAdvanceDate && (
        <div className={`auto-advance-toast ${toastExiting ? 'leaving' : ''}`} role="status" aria-live="polite">
          <span className="auto-advance-toast-text">
            Showing next available: <strong>{autoAdvanceDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</strong>
          </span>
          <button className="auto-advance-toast-close" onClick={dismissToastNow} aria-label="Dismiss">✕</button>
        </div>
      )}

      {/* Week Calendar */}
      {weekStart && (
        <div className="week-calendar">
          <div className="week-header">
            <h3 className="week-month">{formatMonthYear(weekStart)}</h3>
            <div className="week-nav">
              <button className="week-nav-btn" onClick={() => onWeekNav(-1)} disabled={isPast(weekStart)}>←</button>
              <button className="week-nav-btn" onClick={() => onWeekNav(1)}>→</button>
            </div>
          </div>
          <div className="week-days">
            {getWeekDays(weekStart).map(day => {
              const past = isPast(day) && !isToday(day);
              const avail = getDayAvailability(day);
              const unavailable = avail === false;
              const selected = selectedDate && isSameDay(day, selectedDate);
              const today = isToday(day);
              const upkeep = !past && !unavailable && isUpkeepDate(day);
              return (
                <div key={day.toISOString()}
                  className={`week-day ${selected ? 'selected' : ''} ${past || unavailable ? 'disabled' : ''} ${today ? 'today' : ''} ${upkeep ? 'upkeep' : ''}`}
                  onClick={() => !past && !unavailable && onDateSelect(day)}>
                  <span className="week-day-name">{formatDayName(day)}</span>
                  <span className="week-day-num">{day.getDate()}</span>
                  {!past && !unavailable && avail !== null && (
                    <span className={`availability-dot ${avail ? 'available' : ''}`}></span>
                  )}
                  {unavailable && <span className="day-closed">—</span>}
                  {upkeep && <span className="upkeep-pill">Upkeep</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Time Slots */}
      <div className="time-slots-container">
        {/* Relaxed-preferences banner */}
        {!loadingSlots && relaxedServiceLabels.length > 0 && (
          <div className="slot-relaxed-banner">
            <span>
              Showing times where{' '}
              {relaxedServiceLabels.map((s, i) => (
                <React.Fragment key={s.id}>
                  {i > 0 && (i === relaxedServiceLabels.length - 1 ? ' and ' : ', ')}
                  <strong>{s.name}</strong>
                </React.Fragment>
              ))}
              {' '}{relaxedServiceLabels.length === 1 ? 'is' : 'are'} with another provider
            </span>
            <button type="button" className="slot-relaxed-reset" onClick={onResetRelaxed}>Reset</button>
          </div>
        )}

        {/* Soft prompt: 1-2 slots, at least one preferenced service still strict */}
        {!loadingSlots && availableSlots.length >= 1 && availableSlots.length <= 2 && preferencedServices.length > 0 && !softPromptDismissed && (
          <div className="slot-soft-prompt">
            <p>
              Want to see more times? We can find times where <strong>{preferencedServices[0].name}</strong> is with another provider.
            </p>
            <div className="slot-prompt-actions">
              <button type="button" className="slot-prompt-primary" onClick={() => onRelaxService(preferencedServices[0].id)}>Show me</button>
              <button type="button" className="slot-prompt-secondary" onClick={() => setSoftPromptDismissed(true)}>No thanks</button>
            </div>
          </div>
        )}

        {/* Strong prompt: zero slots, at least one preferenced service still strict */}
        {!loadingSlots && availableSlots.length === 0 && preferencedServices.length > 0 && (
          <div className="slot-empty-prompt">
            <p>No times work with all your provider choices. Want to try with first-available providers?</p>
            <button type="button" className="slot-prompt-primary" onClick={onRelaxAllServices}>Yes, find me a time</button>
          </div>
        )}

        {loadingSlots ? (
          <Spinner text="Loading available times..." />
        ) : availableSlots.length === 0 ? (
          preferencedServices.length === 0 && (
            <div className="no-slots-message">
              <p>No available times on this day.</p>
              <p className="no-slots-hint">Try selecting a different date.</p>
            </div>
          )
        ) : (() => {
          const { morning, afternoon, evening } = groupSlotsByPeriod(availableSlots);
          const recommended = getRecommendedTimes(availableSlots);
          return (
            <div className="time-periods">
              {/* Recommended Times */}
              {recommended.length > 0 && (
                <div className="recommended-times">
                  <h4 className="recommended-label">✦ RECOMMENDED TIMES</h4>
                  <div className="recommended-grid">
                    {recommended.map(slot => (
                      <button key={'rec-' + slot.Time}
                        className={`time-slot recommended ${selectedTime === slot.Time ? 'selected' : ''}`}
                        onClick={() => onTimeSelect(slot)}>
                        {formatTime(slot.Time)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {morning.length > 0 && (
                <div className="time-period">
                  <h4 className="period-label">Morning</h4>
                  <div className="time-grid">
                    {morning.map(slot => (
                      <button key={slot.Time}
                        className={`time-slot ${selectedTime === slot.Time ? 'selected' : ''}`}
                        onClick={() => onTimeSelect(slot)}>
                        {formatTime(slot.Time)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {afternoon.length > 0 && (
                <div className="time-period">
                  <h4 className="period-label">Afternoon</h4>
                  <div className="time-grid">
                    {afternoon.map(slot => (
                      <button key={slot.Time}
                        className={`time-slot ${selectedTime === slot.Time ? 'selected' : ''}`}
                        onClick={() => onTimeSelect(slot)}>
                        {formatTime(slot.Time)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {evening.length > 0 && (
                <div className="time-period">
                  <h4 className="period-label">Evening</h4>
                  <div className="time-grid">
                    {evening.map(slot => (
                      <button key={slot.Time}
                        className={`time-slot ${selectedTime === slot.Time ? 'selected' : ''}`}
                        onClick={() => onTimeSelect(slot)}>
                        {formatTime(slot.Time)}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default TimeSelectionStep;
